/**
 * React Query mutation hook for uploading images to ShowNotes blocks
 */

import { useMutation } from '@tanstack/react-query'
import httpClient, { pluckData } from 'src/utilities/http-client'
import axios from 'axios'

interface UploadImageRequest {
  blockID: string
  file: File
}

/**
 * Get presigned URL for image upload
 */
async function getPresignedUrl(
  blockID: string,
  basePath: string,
): Promise<string> {
  let response = await httpClient.put(
    `/api/${basePath}/blocks/${blockID}/image`,
  )
  return pluckData(response)
}

/**
 * Upload image to S3 using presigned URL
 */
async function uploadImageToS3(
  presignedUrl: string,
  file: File,
): Promise<void> {
  await axios.put(presignedUrl, file, {
    headers: {
      'Cache-Control': 'max-age=2592000',
      'content-type': 'image/jpeg',
    },
  })
}

/**
 * Complete image upload process
 * Backend stores the S3 key after upload completes
 */
async function uploadBlockImage(
  { blockID, file }: UploadImageRequest,
  basePath: string,
): Promise<void> {
  // Get presigned URL
  let presignedUrl = await getPresignedUrl(blockID, basePath)

  // Validate presigned URL to prevent SSRF attacks
  if (
    typeof presignedUrl !== 'string' ||
    !presignedUrl.startsWith('https://')
  ) {
    // eslint-disable-next-line no-console
    console.error('Invalid presigned URL received')
    return
  }

  let url = new URL(presignedUrl)
  if (!url.hostname.endsWith('.amazonaws.com')) {
    // eslint-disable-next-line no-console
    console.error('Presigned URL must be from S3')
    return
  }
  if (
    !url.searchParams.has('X-Amz-Signature') &&
    !url.searchParams.has('Signature')
  ) {
    // eslint-disable-next-line no-console
    console.error('URL must be a presigned S3 URL')
    return
  }

  // Upload to S3
  await uploadImageToS3(presignedUrl, file)

  // Backend will store the S3 key, frontend just waits for upload to complete
}

/**
 * Hook to upload an image for a show notes block
 * After upload, refetch blocks to get the updated S3 key from backend
 */
export function useUploadBlockImage(basePath: string) {
  return useMutation({
    mutationFn: (request: UploadImageRequest) =>
      uploadBlockImage(request, basePath),
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to upload block image:', error)
    },
  })
}
